import { Injectable } from '@angular/core';
import {
  GenericViewComponent,
  GenericViewDataModel,
  GenericViewDisplayConfig,
  GenericViewGroup,
} from '@osos/shared/api';
import {
  ApplicationSharingLevelEnum,
  GenericViewPresenterModel,
  PageContainerLayoutMode,
  PageDisplayOptions,
} from '@osos/shared/types';
import { ParserBase } from '../../bases';

type PageContainerLayoutType = { mode: PageContainerLayoutMode };

@Injectable()
export class GenericViewsParserV1Service extends ParserBase<
  GenericViewDataModel,
  GenericViewPresenterModel
> {
  constructor() {
    super(GenericViewDataModel, GenericViewPresenterModel, 1);
  }

  getPresenterModel(model: GenericViewDataModel): GenericViewPresenterModel {
    return {
      id: model.id,
      specificationVersion: model.specificationVersion,
      name: model.name,
      description: model.description,
      icon: model.icon,
      anonymous: model.anonymous,
      domain: model.domain,
      authenticated: model.authenticated,
      isPublished: model.isPublished,
      relatedTo: model.relatedTo,
      isPinned: model.isPinned,
      pageType: model.pageType,
      embeddedPageConfig: model.embeddedPageConfig,
      sidebarConfig: model?.sideBarConfig ?? {
        openMode: 0,
      },
      displayConfig: model?.displayConfig
        ? this._mapDisplayConfigToPresenterModel(model.displayConfig)
        : this._getDefaultDisplayOptions(),
      displayOrder: model.displayOrder,
      ...(model.permissions && { permissions: model.permissions }),
      ...(model.components && { components: model.components }),
      ...(model.groups && { groups: model.groups }),
      uiAttributes: model.uiAttributes,
      allowExportPDF: model.allowExportPDF,
      autoRefresh: model.autoRefresh?.enabled,
      autoRefreshInterval: model.autoRefresh?.interval,
      sharing: {
        ...model.sharing,
        level: model.sharing.level.id as ApplicationSharingLevelEnum,
      },
      redirectionRules: model.redirectionRules
        ? JSON.stringify(model.redirectionRules)
        : undefined,
      defaultPageForSpaces:
        model.sharing.level.id === ApplicationSharingLevelEnum.Private ||
        model.sharing.level.id === ApplicationSharingLevelEnum.Shared
          ? []
          : (model.defaultPageForSpaces ?? []),
      updateIdentifier: model.updateIdentifier,
      help: model.help,
    };
  }
  getDataModel(presenter: GenericViewPresenterModel): GenericViewDataModel {
    return {
      specificationVersion: presenter.specificationVersion || 1,
      name: presenter.name,
      description: presenter.description,
      anonymous: presenter.anonymous as GenericViewDataModel['anonymous'],
      authenticated: presenter.authenticated,
      isPublished: presenter.isPublished,
      relatedTo: presenter.relatedTo,
      isPinned: presenter.isPinned,
      domain: presenter.domain,
      icon: presenter.icon?.name ? presenter.icon : null,
      sideBarConfig: presenter.sidebarConfig,
      displayConfig: this._mapDisplayConfigToDataModel(presenter.displayConfig),
      displayOrder: presenter.displayOrder ?? undefined,
      permissions: presenter.permissions ? presenter.permissions : undefined,
      components: presenter.components
        ? presenter.components.map(
            (cmp) =>
              ({
                ...cmp,
                icon: cmp.icon?.name ? cmp.icon : null,
              }) as GenericViewComponent,
          )
        : undefined,
      groups: presenter.groups
        ? (presenter.groups as GenericViewGroup[])
        : undefined,
      uiAttributes: presenter.uiAttributes,
      allowExportPDF: presenter.allowExportPDF,
      autoRefresh:
        presenter.autoRefresh !== undefined && presenter.autoRefresh !== null
          ? {
              enabled: presenter.autoRefresh,
              interval: presenter.autoRefreshInterval,
            }
          : undefined,
      pageType: presenter.pageType,
      embeddedPageConfig: presenter.embeddedPageConfig,
      sharing: {
        ...presenter.sharing,
        level: {
          id: presenter.sharing.level,
        },
      },
      redirectionRules: presenter?.redirectionRules
        ? JSON.parse(presenter.redirectionRules)
        : undefined,
      defaultPageForSpaces:
        presenter.sharing.level === ApplicationSharingLevelEnum.Private ||
        presenter.sharing.level === ApplicationSharingLevelEnum.Shared
          ? []
          : (presenter.defaultPageForSpaces ?? []),
      updateIdentifier: presenter.updateIdentifier,
      help: presenter.help?.contextualHelp ? presenter.help : null,
    };
  }

  private _mapDisplayConfigToPresenterModel(
    config: GenericViewDisplayConfig,
  ): PageDisplayOptions {
    const { pageContainerLayout, ...model } = config;

    return {
      ...model,
      pageContainedWidth:
        pageContainerLayout?.mode === PageContainerLayoutMode.Contained,
    };
  }

  private _mapDisplayConfigToDataModel(
    config: PageDisplayOptions,
  ): GenericViewDisplayConfig {
    const { pageContainedWidth, ...model } = config;

    return {
      ...model,
      pageContainerLayout: {
        mode: pageContainedWidth
          ? PageContainerLayoutMode.Contained
          : PageContainerLayoutMode.Fluid,
      } satisfies PageContainerLayoutType,
    };
  }

  private _getDefaultDisplayOptions(): PageDisplayOptions {
    return {
      transparent: false,
      fullPage: false,
      outerSpacing: true,
      showPageHeader: true,
      pageContainedWidth: false,
    };
  }
}
